import { getFloatWithPrecision } from '../../services/helpers';
import { IMaterialTableProps } from '../../components/Table';
import WidthTypeEnum from '../../enums/VirtualizedTableSupportedColumnWidthTypeEnum';

interface IParseColumnWidthReturn {
  width: number;
  valueType: WidthTypeEnum.PX | WidthTypeEnum.PERCENT;
}

type TColumn<RowData extends object> = IMaterialTableProps<RowData>['columns'][number];

const throwInvalidWidthError = (width: unknown) => {
  throw new Error(
    `Virtualized table: unsupported column width '${
      width
    }' provided. Either specify the width as a string with '${WidthTypeEnum.PX}' or '${
      WidthTypeEnum.PERCENT
    }' value type, provide a number which will be treated as '${
      WidthTypeEnum.PX
    }', or completely remove width for needed fields.`,
  );
};

export const parseVirtualizedTableColumnWidth = (
  width: string | number,
): IParseColumnWidthReturn => {
  if (typeof width === 'number') {
    const processedWidth = getFloatWithPrecision(width);
    return { width: processedWidth, valueType: WidthTypeEnum.PX };
  }

  let valueType;
  if (width.endsWith(WidthTypeEnum.PX)) {
    valueType = WidthTypeEnum.PX;
  }
  if (width.endsWith(WidthTypeEnum.PERCENT)) {
    valueType = WidthTypeEnum.PERCENT;
  }
  if (!valueType) {
    return throwInvalidWidthError(width);
  }

  const parsedWidth = parseFloat(width);
  if (Number.isNaN(parsedWidth)) {
    return throwInvalidWidthError(width);
  }
  const processedWidth = getFloatWithPrecision(parsedWidth);
  return { width: processedWidth, valueType };
};

export const fillVirtualizedTableColumnWidth = <RowData extends object>(
  column: TColumn<RowData>,
  width: number,
): TColumn<RowData> => {
  const processedWidth = `${width}px`;
  return {
    ...column,
    width: processedWidth,
    // @ts-ignore
    tableData: { ...column.tableData, width: processedWidth },
  };
};
